import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  LoadingState,
  Metric,
} from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { vendorApi } from "../../lib/api";
import { VendorDemoBanner, VendorLiveBadge } from "../../components/VendorMode";
import { CRITICALITY_LABEL, STATE_LABEL, TIER_LABEL } from "./labels";

function Counts({ title, counts, labels }: { title: string; counts: Record<string, number>; labels: Record<string, string> }) {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  return (
    <Card>
      <CardHeader title={title} />
      <CardBody>
        {entries.length === 0 ? (
          <p style={{ color: "var(--ods-color-muted)" }}>Nothing registered yet.</p>
        ) : (
          <div className="metric-grid">
            {entries.map(([key, n]) => (
              <div className="metric-card" key={key}>
                <Metric value={n} label={labels[key] ?? key} />
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function VenDashboard() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(
    async () => {
      const [dashboard, pending] = await Promise.all([
        vendorApi.dashboard(orgId),
        vendorApi.intakeRequests(orgId, { status: "PENDING" }),
      ]);
      return { dashboard, pending };
    },
    [orgId],
  );

  const d = state.data?.dashboard.data.dashboard;
  const pending = state.data?.pending.data.requests.length ?? 0;
  const demoMode = state.data?.dashboard.demoMode ?? false;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Vendor AI</h1>
          <p>Who you buy from, what they touch, and what still has to be decided or evidenced.</p>
        </div>
        <div className="ods-row">
          <Link to="/studio/vendor-ai/vendors">
            <Button size="sm">Vendor register</Button>
          </Link>
          <Link to="/studio/vendor-ai/intake">
            <Button size="sm" variant="secondary">
              Intake queue
            </Button>
          </Link>
        </div>
      </div>

      <VendorDemoBanner show={demoMode} />
      <div style={{ marginBottom: "var(--ods-space-3)" }}>
        <VendorLiveBadge demoMode={demoMode} />
      </div>
      {state.loading && <LoadingState label="Loading the vendor portfolio…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {d && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={d.totalVendors} label="Vendors registered" />
            </div>
            <div className="metric-card">
              <Metric value={pending} label="Awaiting a decision" tone="var(--ods-color-warning)" />
            </div>
            <div className="metric-card">
              <Metric value={d.unowned} label="Without an owner" tone="var(--ods-color-warning)" />
            </div>
            <div className="metric-card">
              <Metric value={d.withRegulatedData} label="Handling regulated data" />
            </div>
          </div>
          <Counts title="By risk tier" counts={d.byRiskTier} labels={TIER_LABEL} />
          <Counts title="By criticality" counts={d.byCriticality} labels={CRITICALITY_LABEL} />
          <Counts title="By lifecycle state" counts={d.byLifecycleState} labels={STATE_LABEL} />
        </>
      )}
    </>
  );
}
